import type { ReactNode } from 'react'
import { Input, Label, ListBox, ListBoxItem, Select } from '@heroui/react'

import type { SelectOption } from '@/components/ui/form-fields'

/* -------------------------------------------------------------------------- */
/* Fields                                                                     */
/* -------------------------------------------------------------------------- */

/** The label over every field. */
const FIELD_LABEL = 'text-foreground text-sm font-semibold'

/** Wide enough for "All categories" or a date — a dialog's grid sets its own. */
const FIELD_WIDTH = 'w-full sm:w-44'

interface ReportSelectProps {
  label: string
  options: SelectOption[]
  value: string | null
  onChange: (value: string | null) => void
  /** The "everything" option — "All categories". Omit when a choice is required. */
  allLabel?: string
  isDisabled?: boolean
  /** Its width, in place of the row's fixed one — a dialog's grid sets its own. */
  className?: string
}

/** A key React Aria can hold that stands for "no filter". */
const ALL = 'all'

/**
 * A dropdown in a report's filters, labelled above like every field beside it.
 *
 * `FilterSelect` is the same control with its label beside the trigger, for the
 * module bar; here the labels stack, so the two cannot share markup.
 */
export function ReportSelect({
  label,
  options,
  value,
  onChange,
  allLabel,
  isDisabled,
  className = FIELD_WIDTH,
}: ReportSelectProps) {
  const items = [...(allLabel ? [{ value: ALL, label: allLabel }] : []), ...options]

  return (
    <Select
      selectedKey={value ?? (allLabel ? ALL : null)}
      onSelectionChange={(key) => onChange(key === ALL || key === null ? null : String(key))}
      isDisabled={isDisabled || items.length === 0}
      className={`flex flex-col gap-1 ${className}`}
    >
      <Label className={FIELD_LABEL}>{label}</Label>
      <Select.Trigger className="h-10">
        <Select.Value className="truncate" />
        <Select.Indicator />
      </Select.Trigger>
      <Select.Popover>
        <ListBox>
          {items.map((option) => (
            <ListBoxItem key={option.value} id={option.value} textValue={option.label}>
              {option.label}
            </ListBoxItem>
          ))}
        </ListBox>
      </Select.Popover>
    </Select>
  )
}

interface ReportDateFieldProps {
  label: string
  /** `YYYY-MM-DD`, or empty. */
  value: string
  onChange: (value: string) => void
  min?: string
  max?: string
  /** Shown under the field's own row — "Live" on Stock on hand. */
  action?: ReactNode
  /** Its width, in place of the row's fixed one — a dialog's grid sets its own. */
  className?: string
}

/**
 * A native date field in the field's own clothes.
 *
 * Native because a date picker is one of the few controls every platform already
 * has a better version of than a web app can build — the phone keyboard, the
 * desktop calendar and the locale format all come free, and a manager entering
 * last month's window does it with the control they already know.
 *
 * An empty value is ignored rather than passed up: clearing the field with the
 * keyboard on the way to typing a new date would otherwise ask the server for a
 * period with no end.
 */
export function ReportDateField({
  label,
  value,
  onChange,
  min,
  max,
  action,
  className = FIELD_WIDTH,
}: ReportDateFieldProps) {
  return (
    <label className={`flex flex-col gap-1 ${className}`}>
      <span className="flex items-center justify-between gap-2">
        <span className={FIELD_LABEL}>{label}</span>
        {action}
      </span>
      <Input
        type="date"
        value={value}
        min={min}
        max={max}
        onChange={(event) => {
          if (event.target.value) onChange(event.target.value)
        }}
        className="h-10 w-full tabular-nums"
      />
    </label>
  )
}

